import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { fetchTimeslot } from "features/timeslot/timeslotSlice";
import { fetchServices } from "features/service/serviceSlice";
import { fetchProducts } from "features/product/productSlice";
import ServiceCard from "components/ServiceCard";
import ProductCard from "components/ProductCard";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { Address, VehicleInformation } from "data/types";
import { createOrder } from "features/orders/orderSlice";
import {
  addNewAddress,
  addNewVehicle,
  fetchAddress,
  fetchVehicle,
  updateAddressDefault,
  updateVehicleDefault,
} from "features/user/userSlice";
import AddressForm from "components/AddressForm";
import Modal from "shared/Modal/Modal";
import MapWithAutocomplete from "components/MapWithAutocomplete";
import { fetchPromoCodes, applyPromoCode } from "features/promocode/promoCodeSlice";
import { useNavigate } from "react-router-dom";
import { Elements, useElements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import VehicleForm from "components/VehicleForm";
import { FaSpinner } from 'react-icons/fa';
import { notifications } from "utils/notifications";

export interface Timeslot {
  _id: string;
  slotTime: string;
  isAvailable: boolean;
}

interface BookingDetail {
  address: string;
  service: string;
  product: string;
  dateTime: string;
}
interface AddressInfo {
  addresses: Address[];
}

interface CartItem {
  id: string;
  type: "Package" | "Service" | "Product";
  name: string;
  price: number;
  quantity: number;
}

interface OrderData {
  car: VehicleInformation | null;
  date: Date | null;
  time: Timeslot | null;
  cartItems: CartItem[];
  address: Address | null;
  subtotal: number;
  discount: number;
  total: number;
}

let addressInfo: AddressInfo | null;

const isToday = (date: Date) => {
  const today = new Date();
  return date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
};

const getCurrentTimeInDubai = () => {
  // Dubai is UTC+4
  const dubaiOffset = 4;
  const now = new Date();
  const utc = now.getTime() + (now.getTimezoneOffset() * 60000);
  return new Date(utc + (3600000 * dubaiOffset));
};

const CartScreen: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [isNewAddress, setIsNewAddress] = useState(false); // Track new address
  const [isNewVehicle, setIsNewVehicle] = useState(false);

  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [selectedTimeslot, setSelectedTimeslot] = useState<Timeslot | null>(
    null
  );
  const [newAddress, setNewAddress] = useState<Address>({
    name: "",
    street: "",
    city: "",
    country: "UAE",
    postalCode: "",
    isDefault: false,
  });
  const [isAddressModalOpen, setIsAddressModalOpen] = useState<boolean>(false);
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState<boolean>(false);
  const { items } = useAppSelector((state) => state.cart);
  console.log(items);
  const packagesInCart = items.filter((item) => item.type === "Package");
  const servicesInCart = items.filter((item) => item.type === "Service");
  const productsInCart = items.filter((item) => item.type === "Product");

  const [promoCodeInput, setPromoCodeInput] = useState("");

  const [discount, setDiscount] = useState(0);
  const calculateSubtotal = (cartItems: CartItem[]) => {
    const sum = cartItems.reduce((acc, item) => {
      return acc + (item.price * item.quantity);
    }, 0);
    return Number(sum.toFixed(2));
  };

  const openAddressModal = () => setIsAddressModalOpen(true);
  const closeAddressModal = () => setIsAddressModalOpen(false);

  const openVehicleModal = () => setIsVehicleModalOpen(true);
  const closeVehicleModal = () => setIsVehicleModalOpen(false);

  const handleSaveNewAddress = async (address: Address) => {
    console.log("New Address", address);
    delete address._id;
    dispatch(addNewAddress(address));
    closeAddressModal();
    setIsNewAddress(false);
  };

  const handleCancelNewAddress = () => {
    setIsNewAddress(false);
    closeAddressModal(); // Close the modal

  };

  const handleAddressSelect = (addressDetails: Address) => {
    setNewAddress(addressDetails);
  };

  const handleAddVehicle = () => {
    setIsNewVehicle(true);
  };

  useEffect(() => {
    const subtotalValue = Number(calculateSubtotal(items).toFixed(2));
    setSubtotal(subtotalValue);

    const totalValue = Number((subtotalValue - discount).toFixed(2));
    setTotal(totalValue);
  }, [items, discount]);

  const [subtotal, setSubtotal] = useState(calculateSubtotal(items));
  const [total, setTotal] = useState(subtotal);

  const { userInfo, addressInfo, vehicleInfo } = useAppSelector(
    (state) => state.users
  );

  const { promocodes, error: promoCodeError } = useAppSelector((state) => state.promocodes);
  console.log(promocodes)

  const [selectedAddress, setSelectedAddress] = useState<Address | null>(null);
  const [selectedVehicle, setSelectedVehicle] =
    useState<VehicleInformation | null>(null);

  const {
    orders,
    loading: loadingOrder,
    error: errorOrder,
  } = useAppSelector((state) => state.orders);

  useEffect(() => {
    if (addressInfo) {
      const defaultAddress = addressInfo.find((address) => address.isDefault);
      setSelectedAddress(defaultAddress || null);
    }
    if (vehicleInfo) {
      const defaultVehicle = vehicleInfo.find((vehicle) => vehicle.isDefault);
      setSelectedVehicle(defaultVehicle || null);
    }
  }, [addressInfo, vehicleInfo]);

  const { timeslots, loading, error: timeslotError } = useAppSelector(
    (state) => state.timeslots
  );

  const {
    services,
    loading: loadingServices,
    error: errorServices,
  } = useAppSelector((state) => state.services);

  const {
    products,
    loading: loadingProducts,
    error: errorProducts,
  } = useAppSelector((state) => state.products);

  useEffect(() => {
    dispatch(fetchTimeslot());
    dispatch(fetchServices());
    dispatch(fetchProducts());
    dispatch(fetchAddress());
    dispatch(fetchVehicle());
    dispatch(fetchPromoCodes());
  }, [dispatch]);

  const initialNewVehicle: VehicleInformation = {
    _id: "",
    make: "",
    model: "",
    year: 0,
    licensePlateNumber: "",
    isDefault: false,
  };

  const handleSaveNewVehicle = async (vehicle: VehicleInformation) => {
    delete vehicle._id;
    dispatch(addNewVehicle(vehicle));
    setIsNewVehicle(false);
  };

  const handleCancelNewVehicle = () => {
    setIsNewVehicle(false);
    closeVehicleModal()
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedAddress = addressInfo?.find(
      (address) => address._id === e.target.value
    );
    setSelectedAddress(selectedAddress || null);
    dispatch(updateAddressDefault(selectedAddress?._id || ""));
  };
  const handleVehicleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedVehicle = vehicleInfo?.find(
      (vehicle) => vehicle._id === e.target.value
    );
    setSelectedVehicle(selectedVehicle || null);
    dispatch(updateVehicleDefault(selectedVehicle?._id || ""));
  };

  const handleSelectTimeslot = (timeslot: Timeslot) => {
    if (selectedTimeslot && selectedTimeslot._id === timeslot._id) {
      setSelectedTimeslot(null); // Deselect if the same timeslot is clicked again
    } else {
      setSelectedTimeslot(timeslot); // Select the new timeslot
    }
  };

  const handleApplyPromoCode = async () => {
    const promoCode = promoCodeInput.toUpperCase();
    
    if (!promoCode.trim()) {
      notifications.warning("Please enter a promo code");
      return;
    }
    
    setIsApplyingPromo(true);
    try {
      // Dispatch the thunk to apply the promo code
      const resultAction = await dispatch(applyPromoCode(promoCode));
  
    if (applyPromoCode.fulfilled.match(resultAction)) {
      const { message } = resultAction.payload; // Get message from the response
  
      // Find the applied promo code from the `promocodes` state
      const appliedPromo = promocodes.find(
        (promo) => promo.code === promoCode && promo.isActive
      );
  
      if (appliedPromo) {
        const { discountType, discountValue } = appliedPromo;
  
        let appliedDiscount = 0;
  
        if (discountType === "PERCENTAGE") {
          appliedDiscount = Number((subtotal * (discountValue / 100)).toFixed(2)); // Calculate percentage discount
        } else if (discountType === "FIXED") {
          appliedDiscount = discountValue; // Use fixed discount value
        }
  
        // Ensure discount does not exceed subtotal
        appliedDiscount = Math.min(appliedDiscount, subtotal);
  
        setDiscount(appliedDiscount);
        const calculatedTotal = Number((subtotal - appliedDiscount).toFixed(2));
        setTotal(calculatedTotal);
  
        notifications.success(`${message} Discount: ${appliedDiscount} AED`);
      } else {
        // Handle case where promo code is not found or not active
        notifications.error("Invalid or expired promo code");
      }
    } else {
      // Handle error
      notifications.error(resultAction.payload || "Failed to apply promo code");
    }
    } catch (error) {
      notifications.error("Failed to apply promo code. Please try again.");
    } finally {
      setIsApplyingPromo(false);
    }
  };
  
  

  const stripePromise = loadStripe(
    `${process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY}`
  );
  const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const orderData = {
    car: selectedVehicle,
    date: selectedDate,
    time: selectedTimeslot,
    cartItems: items,
    address: selectedAddress,
    subtotal,
    discount,
    total,
  };

  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);

  const handlePayment = async () => {
    try {
      setIsProcessingPayment(true);

      if (!selectedTimeslot || !selectedDate || !selectedAddress || !selectedVehicle) {
        notifications.warning("Please select a timeslot, date, address, and vehicle before proceeding to payment.");
        return;
      }

      // Log the order data being sent
      console.log("Sending order data:", orderData);

      // Show order creation progress
      const orderToastId = notifications.loading("Creating your order...");
      
      // Create order first
      const orderResult = await dispatch(createOrder(orderData)).unwrap();
      console.log("Order creation response:", orderResult);

      if (!orderResult?._id) {
        notifications.dismiss(orderToastId);
        throw new Error("Failed to create order - no order ID received");
      }
      
      // Update toast to show payment processing
      notifications.update(orderToastId, "Order created! Redirecting to payment...");

      const orderId = orderResult._id;
      console.log("Created order with ID:", orderId);

      // Create checkout session
      const sessionData = {
        orderId,
        items: [{
          name: "Car Service Booking",
          price: total,
          quantity: 1
        }]
      };
      console.log("Sending checkout session data:", sessionData);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/payment/create-checkout-session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${localStorage.getItem('token')}`
        },
        credentials: "include",
        body: JSON.stringify(sessionData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("Payment session creation failed:", errorData);
        throw new Error(errorData.message || `Payment session creation failed: ${response.status}`);
      }

      const data = await response.json();
      console.log("Checkout session response:", data);

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Payment URL missing from response");
      }
    } catch (error: any) {
      console.error("Detailed payment error:", {
        message: error.message,
        stack: error.stack,
        error
      });
      notifications.error(`Payment processing failed: ${error.message}`);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Filter available timeslots based on selected date and current time
  const getAvailableTimeslots = () => {
    if (!selectedDate) return [];

    const currentTime = getCurrentTimeInDubai();

    return timeslots.filter(timeslot => {
      if (!timeslot.isAvailable) return false;

      // If selected date is today, only show future timeslots
      if (isToday(selectedDate)) {
        const [hours, minutes] = timeslot.slotTime.match(/(\d+):(\d+)/)?.slice(1) || [];
        const slotHour = parseInt(hours);
        const isPM = timeslot.slotTime.includes('PM');
        const slot24Hour = isPM && slotHour !== 12 ? slotHour + 12 : slotHour;

        const slotDate = new Date(selectedDate);
        slotDate.setHours(slot24Hour, parseInt(minutes));

        return slotDate > currentTime;
      }

      return true;
    });
  };

  const [carAuthChecked, setCarAuthChecked] = useState(false);
  const [spaceChecked, setSpaceChecked] = useState(false);

  const isPaymentEnabled =
    selectedTimeslot &&
    selectedDate &&
    selectedAddress &&
    selectedVehicle &&
    carAuthChecked &&
    spaceChecked;

  return (
    <div className="container mx-auto flex flex-col md:flex-row w-full min-h-screen ">
      <div className="w-full md:w-4/6 p-4 text-white p-2 rounded-lg bg-slate-950 mr-2">
        <label
          htmlFor="bookingDetails"
          className="block text-xxl font-bold text-white mt-4 text-center "
        >
          <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
            Customize Your Booking
          </span>
        </label>
        <div className="text-center text-yellow-500">
          Set details before proceeding to pay: Car,Address,Timeslot etc
        </div>
        <div className="flex items-center mt-4 space-x-4">
          <div>
            <label
              htmlFor="carMake"
              className="block text-lg font-medium text-white mb-1"
            >
              Select Car
            </label>
            <div className="inline-flex items-center space-x-4">
              <select
                id="carMake"
                value={selectedVehicle?._id}
                className="mt-1 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                onChange={handleVehicleChange}
              >
                {vehicleInfo?.map((vehicle) => (
                  <option key={vehicle._id} value={vehicle._id}>
                    {vehicle.make} - {vehicle.model} - {vehicle.year}
                  </option>
                ))}
              </select>
              <ButtonPrimary
                className="mt-1 whitespace-nowrap"
                onClick={openVehicleModal}
              >
                Add Car
              </ButtonPrimary>
              {isVehicleModalOpen && (
                <Modal isOpen={isVehicleModalOpen} onClose={closeVehicleModal}>
                  <div className="m-4">
                    <VehicleForm
                      initialValues={initialNewVehicle}
                      onSubmit={handleSaveNewVehicle}
                      onCancel={handleCancelNewVehicle}
                    />
                  </div>
                </Modal>
              )}
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label
            htmlFor="date"
            className="block text-lg font-medium text-white mt-4"
          >
            Select Date
          </label>
          <DatePicker
            selected={selectedDate}
            onChange={(date: Date) => setSelectedDate(date)}
            minDate={new Date()}
            placeholderText="Select a date"
            dateFormat="MMMM d, yyyy"
            className="form-input rounded-md px-3 py-2 border border-gray-300"
          />
        </div>
        <div className="mt-4">
          <label
            htmlFor="timeSlot"
            className="block text-lg font-semibold text-white mt-4"
          >
            Select Timeslot
          </label>
          <div className="flex mt-4 space-x-4 overflow-auto">
            {getAvailableTimeslots().map((timeslot) => (
              <button
                key={timeslot._id}
                className={`w-32 p-4 rounded-lg text-center text-black font-medium focus:outline-none flex-shrink-0 ${selectedTimeslot && selectedTimeslot._id === timeslot._id
                  ? "bg-primary-700"
                  : "bg-neutral-400"
                  }`}
                onClick={() => handleSelectTimeslot(timeslot)}
              >
                {timeslot.slotTime}
              </button>
            ))}
            {getAvailableTimeslots().length === 0 && (
              <p className="text-yellow-500">No available timeslots for this date</p>
            )}
          </div>
        </div>
        <div className="pt-4">
          <div
            className="flex items-center p-4 mb-4 text-sm text-neutral-900 bg-neutral-50 rounded-lg"
            role="alert"
          >
            <span className="flex-1">
              <p className="font-semibold inline">Note:</p> Free cancellation
              until 12 hours before the start of your booking.{" "}
            </span>
          </div>
        </div>

        <div className="mt-4">
          <label
            htmlFor="services"
            className="block text-lg font-semibold text-white mt-4"
          >
            Add-on Services
          </label>{" "}
          <div className="flex overflow-x-auto">
            <div className="grid grid-flow-col auto-cols-max gap-6">
              {services.map((service) => (
                <ServiceCard key={service._id} service={service} />
              ))}
            </div>
          </div>
        </div>
        <div className="mt-4">
          <label
            htmlFor="products"
            className="block text-lg font-semibold text-white mt-4"
          >
            Add-on Products
          </label>{" "}
          <div className="flex overflow-x-auto">
            <div className="grid grid-flow-col auto-cols-max gap-6">
              {products.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full md:w-2/6 text-white p-2 rounded-lg bg-slate-950">
        <div>
          <label
            htmlFor="booking-details"
            className="block text-lg font-semibold text-white mt-4"
          >
            Booking Details
          </label>{" "}
          <label htmlFor="address" className="w-1/5 text-md text-white mt-4">
            Address:
          </label>
          <div className="flex items-center mt-4 space-x-4">
            <select
              id="address"
              value={selectedAddress?._id}
              className="my-2 block w-1/3 pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
              onChange={handleAddressChange}
            >
              {addressInfo?.map((address) => (
                <option key={address._id} value={address._id}>
                  {address.name}
                </option>
              ))}
            </select>
            <ButtonPrimary
              className="mt-1 whitespace-nowrap"
              onClick={openAddressModal}
            >
              Add Address
            </ButtonPrimary>
            {isAddressModalOpen && (
              <Modal isOpen={isAddressModalOpen} onClose={closeAddressModal}>
                {" "}
                {/* Increase max width here */}
                <div className="m-4">
                  <MapWithAutocomplete onAddressSelect={handleAddressSelect} />
                  <AddressForm
                    initialValues={newAddress}
                    onSubmit={handleSaveNewAddress}
                    onCancel={handleCancelNewAddress}
                  />
                </div>
              </Modal>
            )}
          </div>
        </div>
        <div className="ml-2">
          {selectedAddress && (
            <div>
              <p>
                {selectedAddress.street}, &nbsp;{selectedAddress.city},
              </p>
              <p>{selectedAddress.postalCode}</p>
              <p>{selectedAddress.country}</p>
            </div>
          )}
        </div>
        <div className="mb-4">
          <label
            htmlFor="cart"
            className="block text-lg font-semibold text-white mt-4"
          >
            Your Cart
          </label>
          <div>
            <h2 className="m-0 p-0 font-bold " style={{ color: "#e67e05" }}>
              Package
            </h2>
            {packagesInCart.map((packages) => (
              <div key={packages.id} className="flex justify-between">
                <p style={{ maxWidth: "70%" }}>
                  {packages.name} x {packages.quantity}
                </p>
                <p>AED &nbsp;{packages.price}</p>
              </div>
            ))}
          </div>

          <div>
            <h2 className="m-0 p-0 font-bold" style={{ color: "#e67e05" }}>
              Services
            </h2>
            {servicesInCart.map((service) => (
              <div key={service.id} className="flex justify-between">
                <p style={{ maxWidth: "70%" }}>
                  {service.name} x {service.quantity}
                </p>
                <p>AED &nbsp;{service.price}</p>
              </div>
            ))}
          </div>

          <div>
            <h2 className="m-0 p-0 font-bold" style={{ color: "#e67e05" }}>
              Products
            </h2>
            {productsInCart.map((product) => (
              <div key={product.id} className="flex justify-between">
                <p style={{ maxWidth: "70%" }}>
                  {product.name} x {product.quantity}
                </p>
                <p> AED &nbsp; {product.price}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between mb-4">
          <h2 className="m-0 p-0 font-bold" style={{ color: "#e67e05" }}>
            Subtotal
          </h2>
          <p className="flex justify-end font-semibold">
            AED &nbsp;{calculateSubtotal(items)}
          </p>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Enter promo code"
            value={promoCodeInput}
            onChange={(e) => setPromoCodeInput(e.target.value)}
            className="p-2 bg-white border border-gray-300 rounded mr-2 mb-4"
          />
          <button
            onClick={handleApplyPromoCode}
            disabled={isApplyingPromo}
            className="px-4 py-2 bg-green-500 text-white text-sm font-medium rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isApplyingPromo ? (
              <>
                <FaSpinner className="animate-spin mr-2" />
                Applying...
              </>
            ) : (
              "Apply"
            )}
          </button>
        </div>
        {promoCodeError && <p className="text-red-500">{promoCodeError}</p>}
        <div>
          <h2 className="text-lg font-semibold mb-2">Payment Summary</h2>
          {discount > 0 && (
            <div>
              {" "}
              <div className="flex justify-between">
                <p>Subtotal:</p>
                <p> {subtotal}</p>
              </div>
              <div className="flex justify-between">
                <p>Promo Code Discount:</p>
                <p> {discount}</p>
              </div>
            </div>
          )}
          <div className="flex justify-between">
            <p>Total:</p>
            <p> {total}</p>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="flex items-center">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="carAuth"
                checked={carAuthChecked}
                onChange={(e) => setCarAuthChecked(e.target.checked)}
                className="h-4 w-4 text-orange-500 focus:ring-0 focus:ring-offset-0 border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div className="ml-2">
              <label htmlFor="carAuth" className="text-sm text-white cursor-pointer">
                I have authorization to wash the car at this location
              </label>
            </div>
          </div>

          <div className="flex items-center">
            <div className="flex items-center h-5">
              <input
                type="checkbox"
                id="spaceCheck"
                checked={spaceChecked}
                onChange={(e) => setSpaceChecked(e.target.checked)}
                className="h-4 w-4 text-orange-500 focus:ring-0 focus:ring-offset-0 border-gray-300 rounded cursor-pointer"
              />
            </div>
            <div className="ml-2">
              <label htmlFor="spaceCheck" className="text-sm text-white cursor-pointer">
                There is enough space around the car for cleaning
              </label>
            </div>
          </div>
        </div>
        <button
          onClick={handlePayment}
          className={`mt-4 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded hover:bg-orange-600 w-full flex items-center justify-center ${!isPaymentEnabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          disabled={isProcessingPayment || !isPaymentEnabled}
        >
          {isProcessingPayment ? (
            <>
              <FaSpinner className="animate-spin mr-2" />
              Processing...
            </>
          ) : (
            "Proceed to Payment"
          )}
        </button>
      </div>
    </div>
  );
};

export default CartScreen;
